/*
 * MapLoader - loads project markers onto a Leaflet map via Algolia search.
 *
 * Updated for algoliasearch v5 API:
 * - Removed client.initIndex() (no longer exists in v5)
 * - client.getObject() replaces index.getObject()
 * - client.search() format updated for v5
 */

/** Escape a string for safe insertion into HTML attributes and text. */
function _esc(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

window.MapLoader = {
  initialize(map, client, indexName, opts) {
    this.map = map;
    this.client = client;
    this.indexName = indexName;
    this.currentRequestCounter = 0;
    // Store context that was previously implicit globals
    this._isSelfCompany = (opts && opts.isSelfCompany) || false;
    this._userMembership = (opts && opts.userMembership) || '';
    this._trial = (opts && opts.trial) || false;
    return this;
  },

  load(queryGroups, nbPages) {
    this.counter = 0;
    this.nbPages = nbPages;
    this.currentRequestCounter += 1;
    return _.map(queryGroups, queryGroup => {
      // v5: client.search() accepts the same multi-index query format
      return this.client.search(queryGroup).then(this._wrapWithCancel(this._onSearchResponse.bind(this), this.currentRequestCounter));
    });
  },

  _projectStatus(status, status_text) {
    if (!this._isSelfCompany && (this._userMembership !== 'insights')) {
      status = '';
      status_text = '';
    } else if (!this._isSelfCompany && (this._userMembership === 'insights') && this._trial && ($.inArray(status, ['tender', 'planning', 'under_design', 'under_construction', 'on_hold']) !== -1)) {
      status = 'ongoing';
      status_text = 'Ongoing';
    }
    return {
      status,
      status_text
    };
  },

  _customizedMeta(source) {
    let meta;
    if (this._userMembership !== 'insights') {
      meta = '<span class=\'address\'>' + _esc(source.location_text) + '</span>';
      if (source.end_year != null) {
        meta += '<span class=\'date\'>' + _esc(source.end_year) + '</span>';
      }
    } else {
      ({ meta } = source);
    }
    return meta;
  },

  _drawInfobox(object) {
    const result = this._projectStatus(object.status, object.status_text);
    object.status = result.status;
    object.status_text = result.status_text;

    const status = "<div class='status-with-label'><span class='project-status status-label " + _esc(object.status) + "'>" + _esc(object.status_text) + "</span></div>";

    let construction_types_list = '';
    for (const sector of object.sectors) {
      construction_types_list += '<span data-tooltip class=\'sector-icon powertip ' + _esc(sector) + '\' title=\'' + _esc(sector) + '\'></span>';
    }

    let budget = '';
    if (this._userMembership === 'insights') {
      budget += '<div class="icon-list__item" title="Budget"><i class="icon fal fas-sack-dollar"></i><div class="icon-value">' + _esc(object.project_budget) + '</div></div>';
    }

    let meta = '<div class=\'item-content primary-details\'>' + status + '<span class=\'data-list\'><span class=\'data-item\'><div class=\'data-value\'>' + _esc(object.location_text) + '</div></span></span></div>';
    meta += '<div class="item-content card-footer">' + construction_types_list + '<div class="icons-list"><div class="icon-list__item" title="' + _esc(object.number_of_participants) + ' Participants"><i class="icon fal fas-user"></i><div class="icon-value">' + _esc(object.number_of_participants) + '</div></div>' + budget + '</div></div>';

    if (this._userMembership == 'visitor') {
      const verified_pill = object.is_verified ? '<div class="verified powertip" data-powertip="This project has been verified">Verified</div>' : '';

      return '<div class="vcard project-card map-vcard ' + _esc(object.status) + '">' + '<div class="project-card-image-box">' + '<a href="/projects/' + _esc(object.slug) + '" class="image-box-link">' + '<img title="' + _esc(object.title) + '" alt="" data-src="' + 'https://res.cloudinary.com/' + $.cloudinary.config('cloud_name') + '/image/upload/w_auto,dpr_auto,q_auto:eco,c_fill,ar_4:3,g_auto,fl_progressive/' + _esc(object.photo_public_id) + '" class="project-map-image cld-responsive">' + '</a>' + '</div>' + '<div class="project-card-info">' + '<a href="/projects/' + _esc(object.slug) + '" class="vcard-name">' + _esc(object.title) + '</a>' + '<div class="vcard-data">' + this._customizedMeta(object) + '</div>' + '<div class="project-card-icons">' + construction_types_list + '<div class="participants-icon powertip" title="' + _esc(object.number_of_participants) + ' Participants"></div><div class="participants-value">' + _esc(object.number_of_participants) + '</div>' + verified_pill + '</div>' + '</div>' + '</div>';
    }
    else {
      return '<div class="items vertical"><div class="item"><div class="project-card"><div class="item-body single-project ' + _esc(object.status) + '">' + '<div class="item-image item-left project-image">' + '<a href="/projects/' + _esc(object.slug) + '" class="project-image">' + '<img title="' + _esc(object.title) + '" alt="" data-src="' + 'https://res.cloudinary.com/' + $.cloudinary.config('cloud_name') + '/image/upload/w_auto,dpr_auto,q_auto:eco,c_fill,ar_4:3,g_auto,fl_progressive/' + _esc(object.photo_public_id) + '" class="project-map-image cld-responsive">' + '</a>' + '</div>' + '<div class="item-body-wrapper body-column"><div class="item-head"><div class="item-head-wrapper"><div class="item-title">' + '<a href="/projects/' + _esc(object.slug) + '" class="vcard-name card-title ellipsis">' + _esc(object.title) + '</a></div></div></div>' + meta + '</div></div></div></div>';
    }
  },

  _fetchPopupContent(id, e) {
    const popup = e.target.getPopup();

    // v5: use client.getObject() instead of index.getObject()
    return this.client.getObject({ indexName: this.indexName, objectID: id }).then(object => {
      popup.setContent(this._drawInfobox(object));
      popup.update();
      return $.cloudinary.responsive();
    });
  },

  _setMarkers(data, lastPage) {
    if (lastPage == null) { lastPage = false; }
    const markerList = [];
    this._markersCounter += 1;

    if (_.isEmpty(data)) {
      return false;
    }

    _.each(data, (entry, i) => {
      const result = this._projectStatus(entry.status, entry.status_text);
      entry.status = result.status;
      entry.status_text = result.status_text;

      const markerContent = '<div id="map-marker-' + entry.objectID + '" class="map-marker ' + entry.icon_type + ' ' + entry.status + '"> <div class="map-marker-icon"></div></div>';

      const _icon = L.divIcon({
        html: markerContent,
        iconSize: [36, 46],
        iconAnchor: [18, 32],
        popupAnchor: [130, -28],
        className: ''
      });

      const {
        title
      } = entry;
      const marker = L.marker(new L.LatLng(entry['_geoloc'].lat, entry['_geoloc'].lng), {
        title,
        icon: _icon
      });

      const infoboxContent = document.createElement("div");
      marker.bindPopup(infoboxContent);
      marker.on('click', this._fetchPopupContent.bind(this, entry.objectID));

      markerList.push(marker);

      // Set hover states for marker 
      marker.on('popupopen', function () {
        return this._icon.className += ' marker-active';
      });

      return marker.on('popupclose', function () {
        if (this.length > 0) {
          return this._icon.className = 'leaflet-marker-icon leaflet-zoom-animated leaflet-clickable marker-loaded';
        }
      });
    });

    markers.addLayers(markerList);

    // Display markers
    if (lastPage) {
      map.addLayer(markers);
      return map.fitBounds(markers.getBounds());
    }
  },

  _onSearchResponse(response) {
    return _.each(response.results, result => {
      this.counter += 1;
      this._setMarkers(result.hits, this.counter === this.nbPages);
      if (this.counter === this.nbPages) {
        return this.map.spin(false);
      }
    });
  },

  _wrapWithCancel(fn, counter) {
    return data => {
      if (counter === this.currentRequestCounter) {
        return fn(data);
      }
    };
  }
};